import * as Apollo from "@apollo/client";
import { relayStylePagination } from "@apollo/client/utilities";
import { Option, pipe, ReadonlyArray } from "@morphism/fp";
import React from "react";

import { Company } from "~/Company";
import { Driver as GenericDriver } from "~/Driver";
import { DVIR, DVIRs } from "~/DVIR";
import { Graph, gql } from "~/Graph";
import { Time } from "~/Time";

export type Query = Graph.Generated.DviRsQuery;
export namespace Query {
  export const DEFAULT_PAGE_SIZE = 100 as const;
  export const DEFAULT_INITIAL_PAGE_SIZE = 30 as const;

  export const emptyConnection = (): Graph.Generated.Driver__VehicleInspectionReport__Connection => ({
    __typename: "Driver__VehicleInspectionReport__Connection",
    totalCount: 0,
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
    },
    edges: undefined,
  });

  export const decode = (
    query: Query
  ):
    | Graph.Generated.Driver__VehicleInspectionReport__Connection
    | Graph.Errors => {
    const { companies } = query;
    if (companies && "vehicleInspectionReports" in companies) {
      if (companies?.vehicleInspectionReports?.bySubmittedDateRange) {
        if (
          companies?.vehicleInspectionReports?.bySubmittedDateRange
            ?.__typename === "Driver__VehicleInspectionReport__Connection"
        ) {
          return (companies.vehicleInspectionReports?.bySubmittedDateRange ??
            Query.emptyConnection()) as Graph.Generated.Driver__VehicleInspectionReport__Connection;
        } else if (
          "message" in companies?.vehicleInspectionReports?.bySubmittedDateRange
        ) {
          return Graph.Errors.Fragment.decode(
            companies?.vehicleInspectionReports?.bySubmittedDateRange
          );
        }
      }
    }
    return Query.emptyConnection();
  };

  export const typePolicies = (): Apollo.TypePolicies => ({
    Driver__VehicleInspectionReport__ByCompanyQueries: {
      fields: {
        bySubmittedDateRange: relayStylePagination(),
      },
    },
  });

  export type useArgs = Time.Interval.Stopped & {
    readonly last?: number;
    readonly before?: string;
    readonly first?: number;
    readonly after?: string;
    readonly reportingLocationIDs?: readonly number[];
    readonly branchIDs?: Company.Branch.Branches.BranchIDs;
  };

  export const fetchVariables = (companyID: string) => ({
    interval: { start, stop },
    last,
    before,
    first,
    after,
    reportingLocationIDs = [],
    branchIDs = [],
  }: useArgs) => {
    const branchFilterValues = Company.Branch.Branches.Filter.parseValues(
      branchIDs
    );

    return {
      variables: {
        companyID,
        start: Time.DateTime.toISO(start),
        stop: Time.DateTime.toISO(stop),
        last,
        before,
        first,
        after,
        reportingLocationIDs: reportingLocationIDs.map((id) => id.toString()),
        branchIDs: branchFilterValues.branchIDs.map(String),
        includeNoBranch: branchFilterValues.includeNoBranch,
      },
    };
  };

  export const useWithMore = ({
    interval,
    last,
    before,
    first,
    after,
    refetch = false,
    reportingLocationIDs = [],
    branchIDs = [],
  }: useArgs & { readonly refetch?: boolean }) => {
    const companyID = Company.use().company.id;
    const fetchVariables = Query.fetchVariables(companyID);

    const {
      data: dataOrError,
      previousData,
      loading,
      fetchMore,
      error,
    } = Graph.useQuery<Query, Graph.Generated.DviRsQueryVariables>(document(), {
      ...fetchVariables({
        interval,
        last,
        before,
        first,
        after,
        reportingLocationIDs,
        branchIDs,
      }),
      fetchPolicy: refetch ? "cache-and-network" : "cache-first",
      nextFetchPolicy: "cache-first",
      returnPartialData: refetch,
    });

    const [connection, graphError] = React.useMemo(() => {
      const data = pipe(dataOrError ?? previousData, (data) =>
        data ? decode(data) : DVIRs.Query.emptyConnection()
      );
      return [
        !("graphError" in data)
          ? Connection.decode(data)
          : Connection.emptyConnection(),
        "graphError" in data ? data : undefined,
      ];
    }, [dataOrError, previousData, interval]);

    const dvirs = React.useMemo(
      () =>
        pipe(
          connection,
          (decodedConnection) =>
            decodedConnection &&
            (decodedConnection?.edges ?? []).map((edge) => edge.dvir),
          ReadonlyArray.mapWithIndex((index, dvir) => ({ key: index, ...dvir }))
        ),
      [connection, interval]
    );

    return {
      loading,
      fetchMore,
      fetchVariables,
      connection,
      dvirs,
      ...Graph.Errors.onError(error, graphError),
    };
  };

  export const document = () => gql`
    ${Graph.Errors.Fragment.document()}
    ${DVIR.Fragment.document()}

    query DVIRs(
      $companyID: ID!
      $start: DateTime!
      $stop: DateTime!
      $last: Int
      $before: String
      $first: Int
      $after: String
      $reportingLocationIDs: [String!]
      $branchIDs: [String!]
      $includeNoBranch: Boolean
    ) {
      companies(id: $companyID) {
        ... on Company {
          id
          vehicleInspectionReports {
            bySubmittedDateRange(
              input: {
                from: $start
                to: $stop
                reporting_location_ids: $reportingLocationIDs
                branchIDs: $branchIDs
                includeNoBranch: $includeNoBranch
              }
              last: $last
              before: $before
              first: $first
              after: $after
            ) @connection(key: "bySubmittedDateRange", filter: ["from", "to"]) {
              ... on Driver__VehicleInspectionReport__Connection {
                totalCount
                pageInfo {
                  hasPreviousPage
                  hasNextPage
                  startCursor
                  endCursor
                }
                edges {
                  cursor
                  node {
                    ...DVIR
                  }
                }
              }
              ... on Error {
                ...Error
              }
            }
          }
        }

        ... on Error {
          ...Error
        }
      }
    }
  `;

  export type Connection = {
    readonly totalCount: number;
    readonly pageInfo: Omit<Graph.Generated.Pagination__PageInfo, "__typename">;
    readonly edges: readonly {
      readonly cursor: string;
      readonly dvir: DVIR;
    }[];
  };

  export namespace Connection {
    export const decode = (
      encoded: Graph.Generated.Driver__VehicleInspectionReport__Connection
    ): Connection => {
      return {
        totalCount: encoded.totalCount || 0,
        pageInfo: encoded.pageInfo,
        edges:
          encoded.edges && encoded.edges.length > 0
            ? encoded.edges.map((edge) => ({
                cursor: edge.cursor,
                dvir: DVIR.Fragment.decode(edge.node),
              }))
            : [],
      };
    };

    export const toDVIRs = (
      connection: Graph.Generated.Driver__VehicleInspectionReport__Connection
    ): DVIRs =>
      pipe(
        connection,
        decode,
        (decodedConnection) =>
          decodedConnection &&
          (decodedConnection?.edges ?? []).map((edge) => edge.dvir)
      );

    export const emptyConnection = (): Connection => ({
      totalCount: 0,
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
      edges: [],
    });
  }

  export namespace Driver {
    export type Query = Graph.Generated.GetDvirForDriverByRangeQuery;

    type Props = GenericDriver & Time.Interval.Stopped;
    export namespace Query {
      export const use = ({ id, interval }: Props) => {
        const { start, stop } = interval;
        const { data: rawData, loading, error } = Graph.useQuery<
          Query,
          Graph.Generated.GetDvirForDriverByRangeQueryVariables
        >(document(), {
          variables: {
            driverID: id,
            start: Time.DateTime.toISO(start),
            stop: Time.DateTime.toISO(stop),
          },
        });

        const decodedData = pipe(
          rawData,
          Option.fromNullable,
          Option.map(decode),
          Option.getOrElse(() => [] as DVIRs | Graph.Errors)
        );

        const [dvirs, graphError] = [
          pipe(decodedData, (data) => (!("graphError" in data) ? data : [])),
          pipe(decodedData, (data) =>
            "graphError" in data ? data : undefined
          ),
        ];

        return { dvirs, loading, error, graphError };
      };

      export const decode = (query: Query): DVIRs | Graph.Errors => {
        const { driver } = query;
        if (driver && "vehicleInspectionReports" in driver) {
          if (driver?.vehicleInspectionReports?.bySubmittedDateRange) {
            if (
              driver?.vehicleInspectionReports?.bySubmittedDateRange[0]
                ?.__typename === "Driver__VehicleInspectionReport"
            ) {
              return pipe(
                (driver.vehicleInspectionReports?.bySubmittedDateRange ??
                  []) as readonly Graph.Generated.DvirFragment[],
                ReadonlyArray.map<Graph.Generated.DvirFragment, DVIR>((dvir) =>
                  DVIR.Fragment.decode(dvir)
                )
              );
            } else if (
              "Error" in driver?.vehicleInspectionReports?.bySubmittedDateRange
            ) {
              return Graph.Errors.Fragment.decode(
                // @ts-ignore
                driver?.vehicleInspectionReports?.bySubmittedDateRange
              );
            }
          }
        }
        return [];
      };

      export const document = () => gql`
        ${Graph.Errors.Fragment.document()}
        ${DVIR.Fragment.document()}

        query GetDVIRForDriverByRange(
          $driverID: ID!
          $start: DateTime!
          $stop: DateTime!
        ) {
          driver(id: $driverID) {
            ... on Driver {
              id
              vehicleInspectionReports {
                bySubmittedDateRange(input: { from: $start, to: $stop }) {
                  ... on Driver__VehicleInspectionReport {
                    ...DVIR
                  }
                  ... on Error {
                    ...Error
                  }
                }
              }
            }
            ... on Error {
              ...Error
            }
          }
        }
      `;
    }
  }
}
